// src/features/mensagens/utils/validators.js
// ============================================================================
// 🛡️ VALIDADORES CENTRALIZADOS — ÚNICA FUENTE DE VERDAD
// 📦 V1 ENTERPRISE — Zero Duplicación · Anti-XSS · WhatsApp-Ready
// ============================================================================
//
// REGLAS DE ORO:
// 1. TODA validación del módulo mensagens pasa por este archivo
// 2. NUNCA hardcodear regex o límites en componentes
// 3. SIEMPRE devolver { valid: boolean, error: string | null }
// 4. Mensajes de error en pt-BR (consistente con el sistema)
// ============================================================================

import { PERFORMANCE } from './constants';

// ============================================================================
// 🎯 TIPO DE RETORNO ESTÁNDAR
// ============================================================================
// Todas las funciones retornan: { valid: boolean, error: string | null }
// Esto permite uso consistente en cualquier componente:
//   const { valid, error } = validateTelefone(phone);
//   if (!valid) showToast(error, 'error');

// ============================================================================
// 📞 VALIDACIÓN DE TELÉFONO (WhatsApp Brasileño)
// ============================================================================

/**
 * Valida número de telefone brasileiro para WhatsApp
 * Regras:
 * - Apenas dígitos (após limpeza)
 * - 10-13 dígitos (DDD + número)
 * - Aceita formato internacional (+55) ou nacional
 * 
 * @param {string} phone - Número de telefone (formato livre)
 * @returns {{ valid: boolean, error: string | null, normalized: string | null }}
 */
export function validateTelefone(phone) {
  // 1. Null/undefined check
  if (phone == null || phone === '') {
    return { valid: false, error: 'Número de telefone é obrigatório', normalized: null };
  }

  // 2. Normalizar: remover tudo exceto dígitos
  const digits = String(phone).replace(/\D/g, '');

  // 3. Comprimento mínimo/máximo
  if (digits.length < 10) {
    return { 
      valid: false, 
      error: `Número muito curto (${digits.length} dígitos). Mínimo 10 dígitos.`, 
      normalized: null 
    };
  }

  if (digits.length > 14) {
    return { 
      valid: false, 
      error: `Número muito longo (${digits.length} dígitos). Máximo 14 dígitos.`, 
      normalized: null 
    };
  }

  // 4. Validar DDD brasileiro (10-99, exceto códigos inexistentes)
  const ddd = digits.length === 10 || digits.length === 11 
    ? digits.slice(0, 2) 
    : digits.length === 12 && digits.startsWith('55')
      ? digits.slice(2, 4)
      : digits.length === 13 && digits.startsWith('55')
        ? digits.slice(2, 4)
        : digits.length === 14 && digits.startsWith('55')
          ? digits.slice(2, 4)
          : null;

  // DDIs brasileiros inválidos (reservados/não atribuídos)
  const DDDS_INVALIDOS = ['00', '20', '23', '25', '26', '29', '30', '36', '39', '40', '50', '52', '56', '57', '58', '59', '60', '70', '72', '76', '78', '80', '90'];
  
  if (ddd && DDDS_INVALIDOS.includes(ddd)) {
    return { valid: false, error: `DDD ${ddd} inválido`, normalized: null };
  }

  // 5. Normalizar para formato internacional +55
  let normalized;
  if (digits.startsWith('55') && digits.length >= 12) {
    normalized = digits; // Já está no formato internacional
  } else if (digits.length === 10 || digits.length === 11) {
    normalized = `55${digits}`; // Adicionar código do Brasil
  } else if (digits.length === 12 && !digits.startsWith('55')) {
    normalized = `55${digits.slice(2)}`; // Substituir código de país
  } else {
    normalized = digits;
  }

  return { valid: true, error: null, normalized };
}

// ============================================================================
// ✉️ VALIDACIÓN DE MENSAJE
// ============================================================================

const MESSAGE_MAX_LENGTH = 4096; // Límite de WhatsApp Business API
const MESSAGE_MIN_LENGTH = 1;

// Caracteres prohibidos por seguridad (prevención XSS básica)
const FORBIDDEN_PATTERNS = [
  { pattern: /<script[\s\S]*?>[\s\S]*?<\/script>/gi, label: 'tags <script>' },
  { pattern: /javascript:/gi, label: 'protocolo javascript:' },
  { pattern: /on\w+\s*=/gi, label: 'event handlers inline' },
];

/**
 * Valida o conteúdo de uma mensagem antes de enviar
 * 
 * @param {string} message - Texto da mensagem
 * @returns {{ valid: boolean, error: string | null, sanitized: string | null }}
 */
export function validateMensagem(message) {
  // 1. Null/undefined check
  if (message == null || message === '') {
    return { valid: false, error: 'A mensagem não pode estar vazia', sanitized: null };
  }

  // 2. Tipo check
  if (typeof message !== 'string') {
    return { valid: false, error: 'Formato de mensagem inválido', sanitized: null };
  }

  // 3. Sanitizar (preservando a função existente do helpers.js)
  const sanitized = message
    .trim()
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]{2,}/g, ' ');

  // 4. Verificar vazio após sanitização
  if (sanitized.length < MESSAGE_MIN_LENGTH) {
    return { valid: false, error: 'A mensagem não pode estar vazia', sanitized: null };
  }

  // 5. Verificar comprimento máximo
  if (sanitized.length > MESSAGE_MAX_LENGTH) {
    return { 
      valid: false, 
      error: `Mensagem muito longa (${sanitized.length}/${MESSAGE_MAX_LENGTH} caracteres)`, 
      sanitized: null 
    };
  }

  // 6. Verificar caracteres proibidos (XSS)
  for (const { pattern, label } of FORBIDDEN_PATTERNS) {
    if (pattern.test(sanitized)) {
      return { valid: false, error: `Conteúdo não permitido detectado (${label})`, sanitized: null };
    }
  }

  // 7. Verificar se NÃO é apenas espaços/emojis vazios
  const visibleChars = sanitized.replace(/[\s\u200B-\u200D\uFEFF]/g, '');
  if (visibleChars.length === 0) {
    return { valid: false, error: 'A mensagem deve conter texto visível', sanitized: null };
  }

  return { valid: true, error: null, sanitized };
}

// ============================================================================
// 📎 VALIDACIÓN DE ARCHIVOS ADJUNTOS (Preparado para WhatsApp API)
// ============================================================================

const ATTACHMENT_LIMITS = {
  image:    { maxSize: 5 * 1024 * 1024,  types: ['image/jpeg', 'image/png', 'image/webp'],     label: 'Imagem' },
  document: { maxSize: 100 * 1024 * 1024, types: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'], label: 'Documento' },
  audio:    { maxSize: 16 * 1024 * 1024,  types: ['audio/mp4', 'audio/mpeg', 'audio/ogg', 'audio/webm'], label: 'Áudio' },
  video:    { maxSize: 16 * 1024 * 1024,  types: ['video/mp4', 'video/webm'],                  label: 'Vídeo' },
};

/**
 * Valida um arquivo anexo antes de enviar
 * 
 * @param {File} file - Arquivo a ser validado
 * @param {'image'|'document'|'audio'|'video'} tipo - Tipo de anexo
 * @returns {{ valid: boolean, error: string | null }}
 */
export function validateAnexo(file, tipo) {
  // 1. Null/undefined check
  if (!file) {
    return { valid: false, error: 'Nenhum arquivo selecionado' };
  }

  // 2. Verificar se o tipo é suportado
  const limits = ATTACHMENT_LIMITS[tipo];
  if (!limits) {
    return { valid: false, error: `Tipo de anexo não suportado: ${tipo}` };
  }

  // 3. Verificar tipo MIME
  if (!limits.types.includes(file.type)) {
    return { 
      valid: false, 
      error: `Formato não suportado. Tipos aceitos: ${limits.types.map(t => t.split('/')[1]).join(', ')}` 
    };
  }

  // 4. Verificar tamanho
  if (file.size > limits.maxSize) {
    const maxMB = limits.maxSize / (1024 * 1024);
    const fileMB = (file.size / (1024 * 1024)).toFixed(1);
    return { 
      valid: false, 
      error: `${limits.label} muito grande (${fileMB}MB). Máximo: ${maxMB}MB` 
    };
  }

  // 5. Verificar tamanho mínimo (evitar arquivos vazios)
  if (file.size === 0) {
    return { valid: false, error: `${limits.label} vazio(a)` };
  }

  return { valid: true, error: null };
}

// ============================================================================
// 👤 VALIDACIÓN DE DESTINATARIO
// ============================================================================

/**
 * Valida dados do destinatário antes de enviar mensagem
 * 
 * @param {Object} destinatario - { clientId, phone, name }
 * @returns {{ valid: boolean, error: string | null }}
 */
export function validateDestinatario(destinatario) {
  // 1. Null/undefined check
  if (!destinatario) {
    return { valid: false, error: 'Destinatário não informado' };
  }

  // 2. Deve ter clientId OU phone
  const hasClientId = destinatario.clientId && typeof destinatario.clientId === 'string';
  const hasPhone = destinatario.phone && typeof destinatario.phone === 'string';

  if (!hasClientId && !hasPhone) {
    return { valid: false, error: 'É necessário informar um cliente ou número de telefone' };
  }

  // 3. Se tem phone, validar
  if (hasPhone && !hasClientId) {
    const phoneValidation = validateTelefone(destinatario.phone);
    if (!phoneValidation.valid) {
      return { valid: false, error: `Telefone inválido: ${phoneValidation.error}` };
    }
  }

  return { valid: true, error: null };
}

// ============================================================================
// 📋 VALIDACIÓN DE REENVÍO
// ============================================================================

/**
 * Valida dados para reenvio de mensagem
 * 
 * @param {Object} params - { messageId, targetClientId, targetPhone }
 * @returns {{ valid: boolean, error: string | null }}
 */
export function validateReenvio({ messageId, targetClientId, targetPhone }) {
  // 1. ID da mensagem original obrigatório
  if (!messageId || typeof messageId !== 'string') {
    return { valid: false, error: 'Mensagem original não identificada' };
  }

  // 2. Destino obrigatório
  if (!targetClientId && !targetPhone) {
    return { valid: false, error: 'Selecione um destinatário para reenviar' };
  }

  // 3. Não reenviar para o mesmo destinatário (validado na camada de negócio)
  // Aqui só validamos estrutura

  return { valid: true, error: null };
}

// ============================================================================
// 🗑️ VALIDACIÓN DE EXCLUSIÓN
// ============================================================================

/**
 * Valida se uma mensagem ou conversa pode ser excluída
 * 
 * @param {Object} params - { type: 'message'|'conversation', id: string }
 * @returns {{ valid: boolean, error: string | null }}
 */
export function validateExclusao({ type, id }) {
  // 1. Tipo obrigatório
  if (!type || !['message', 'conversation'].includes(type)) {
    return { valid: false, error: 'Tipo de exclusão inválido' };
  }

  // 2. ID obrigatório
  if (!id || typeof id !== 'string') {
    return { valid: false, error: 'ID não informado para exclusão' };
  }

  return { valid: true, error: null };
}

// ============================================================================
// 🎯 VALIDADOR DE FORMULÁRIO COMPLETO (Envio de Mensagem)
// ============================================================================

/**
 * Valida todos os campos necessários para enviar uma mensagem
 * 
 * @param {Object} formData - { message, phone, clientId, anexos }
 * @returns {{ valid: boolean, errors: string[], sanitizedMessage: string | null }}
 */
export function validateFormularioEnvio({ message, phone, clientId, anexos = [] }) {
  const errors = [];
  let sanitizedMessage = null;

  // 1. Validar mensagem
  const msgValidation = validateMensagem(message);
  if (!msgValidation.valid) {
    errors.push(msgValidation.error);
  } else {
    sanitizedMessage = msgValidation.sanitized;
  }

  // 2. Validar destinatário
  const destValidation = validateDestinatario({ phone, clientId });
  if (!destValidation.valid) {
    errors.push(destValidation.error);
  }

  // 3. Validar anexos (se houver)
  if (anexos.length > 0) {
    for (const anexo of anexos) {
      const tipo = anexo.type?.startsWith('image') ? 'image' 
        : anexo.type?.includes('pdf') || anexo.type?.includes('document') ? 'document'
        : anexo.type?.startsWith('audio') ? 'audio'
        : anexo.type?.startsWith('video') ? 'video'
        : null;

      if (tipo) {
        const anexoValidation = validateAnexo(anexo, tipo);
        if (!anexoValidation.valid) {
          errors.push(`Anexo: ${anexoValidation.error}`);
        }
      } else {
        errors.push(`Tipo de arquivo não suportado: ${anexo.name}`);
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    sanitizedMessage,
  };
}

// ============================================================================
// 🧊 CONGELAR PARA EVITAR MUTACIONES
// ============================================================================
if (typeof Object.freeze === 'function') {
  Object.freeze(ATTACHMENT_LIMITS);
  Object.freeze(FORBIDDEN_PATTERNS);
}